/**
 * ClinicalHUD 4 teaching profile.
 * kind stays "teaching" so the ClinicalHUD 3 encounter hook keeps commit,
 * on-demand generation, and the no-PHI path. listeningGame swaps the opening.
 */
import { CLINICAL_HUD3_TEACHING } from "features/clinicalHud3/clinicalHud3WorkspaceConfig";
import { HUD3_MODES } from "features/clinicalHud3/clinicalHud3Modes";
import { CLINICAL_HUD4_TEACHING_PATH } from "./clinicalHud4Paths";

export const CLINICAL_HUD4_TEACHING_COLLECTION = "clinicalHud4Teaching";

export const CLINICAL_HUD4_FINAL_CASE = "final-case";

export const CLINICAL_HUD4_MODE_ORDER = Object.freeze([
  "case",
  HUD3_MODES.DIAGNOSTICS,
  CLINICAL_HUD4_FINAL_CASE,
  HUD3_MODES.TREATMENT,
  HUD3_MODES.PEARLS,
  HUD3_MODES.DISCHARGE,
  HUD3_MODES.DOCUMENTATION,
]);

export function nextClinicalHud4Mode(current = "case", order = CLINICAL_HUD4_MODE_ORDER) {
  const list = Array.isArray(order) && order.length ? order : CLINICAL_HUD4_MODE_ORDER;
  const index = list.indexOf(current);
  if (index < 0 || index >= list.length - 1) return "";
  return list[index + 1];
}

export function clinicalHud4ContentReady(mode, snapshot = {}) {
  if (mode === "pearls") {
    const scope = snapshot.pearlsScope;
    return (
      (scope === "actual" || scope === "alternatives") &&
      Array.isArray(snapshot.pearls) &&
      snapshot.pearls.length > 0
    );
  }
  if (mode === "diagnostics") {
    const tests = snapshot.diagnostics?.tests || [];
    return (
      tests.length > 0 || Boolean(String(snapshot.diagnostics?.testingStrategy || "").trim())
    );
  }
  if (mode === "treatment") {
    return Boolean(
      snapshot.treatment?.hasTreatments || snapshot.treatment?.planData?.treatments?.length,
    );
  }
  if (mode === "discharge") {
    return Boolean(
      snapshot.discharge?.hasBeenGenerated || snapshot.discharge?.instructions?.length,
    );
  }
  if (mode === "documentation") {
    const sections = snapshot.notes?.noteSections;
    return Boolean(sections && Object.keys(sections).length);
  }
  return true;
}

function nextContentPending(mode, snapshot = {}, order = CLINICAL_HUD4_MODE_ORDER) {
  const next = nextClinicalHud4Mode(mode, order);
  if (!next || clinicalHud4ContentReady(next, snapshot)) return false;
  if (snapshot.generatingMode === next) return true;
  if (next === "pearls") return !snapshot.pearlsAutoFailed;
  if (next === "diagnostics") return !snapshot.diagnosticsAutoFailed;
  if (next === "treatment") return Boolean(snapshot.treatment?.loading);
  if (next === "discharge") return Boolean(snapshot.discharge?.loading);
  if (next === "documentation") return Boolean(snapshot.notes?.loading);
  return false;
}

export function clinicalHud4ContinueVisible(mode, snapshot = {}, order = CLINICAL_HUD4_MODE_ORDER) {
  if (
    mode === "case" ||
    mode === "choice" ||
    mode === CLINICAL_HUD4_FINAL_CASE ||
    mode === HUD3_MODES.DOCUMENTATION
  ) {
    return false;
  }
  if (mode === "diagnostics" && snapshot.hud4DiagnosticsSource === "stored") return false;
  return Boolean(nextClinicalHud4Mode(mode, order));
}

export function clinicalHud4ContinueReason(mode, snapshot = {}) {
  if (mode === "diagnostics" && snapshot.hud4DiagnosticsSource !== "generated") {
    return "Diagnostic tests are still loading.";
  }
  return "The next section is still loading.";
}

export function clinicalHud4ShowContinue(mode, snapshot = {}, order = CLINICAL_HUD4_MODE_ORDER) {
  if (
    mode === "case" ||
    mode === "choice" ||
    mode === CLINICAL_HUD4_FINAL_CASE ||
    mode === HUD3_MODES.DOCUMENTATION
  ) {
    return false;
  }
  if (!nextClinicalHud4Mode(mode, order)) return false;
  if (mode === "diagnostics" && snapshot.hud4DiagnosticsSource !== "generated") {
    return false;
  }
  return !nextContentPending(mode, snapshot, order);
}

export function clinicalHud4ReachedMode(savedReached, slots, snapshot = {}, order = CLINICAL_HUD4_MODE_ORDER) {
  const orderList = Array.isArray(order) && order.length ? order : CLINICAL_HUD4_MODE_ORDER;
  const saved = orderList.includes(savedReached) ? savedReached : "case";
  const list = Array.isArray(slots) ? slots : [];
  const settled = list.length > 0 && list.every((slot) => slot?.settled);
  if (!settled) return saved;
  const afterCase = nextClinicalHud4Mode("case", orderList);
  const hasFinal = (Array.isArray(snapshot.selectedFinalTitles) ? snapshot.selectedFinalTitles : [])
    .some((title) => String(title || "").trim());
  const normalized = saved === HUD3_MODES.PEARLS && !hasFinal ? afterCase : saved;
  const savedIndex = orderList.indexOf(normalized);
  const earnedIndex = orderList.indexOf(afterCase);
  return savedIndex < earnedIndex ? afterCase : normalized;
}

export function clinicalHud4TabEnabled({
  current,
  target,
  reached,
  forwardReady,
  order = CLINICAL_HUD4_MODE_ORDER,
}) {
  const orderList = Array.isArray(order) && order.length ? order : CLINICAL_HUD4_MODE_ORDER;
  const currentIndex = orderList.indexOf(current);
  const targetIndex = orderList.indexOf(target);
  const reachedIndex = orderList.indexOf(reached || "case");
  if (targetIndex < 0 || currentIndex < 0) return false;
  if (targetIndex <= currentIndex || targetIndex <= reachedIndex) return true;
  return targetIndex === currentIndex + 1 && Boolean(forwardReady);
}

export const CLINICAL_HUD4_TEACHING = Object.freeze({
  ...CLINICAL_HUD3_TEACHING,
  product: "clinicalHud4",
  productTitle: "ClinicalHUD 4",
  productTagline: "Powered by Medical SuperIntelligence",
  collection: CLINICAL_HUD4_TEACHING_COLLECTION,
  listeningGame: true,
  listPath: CLINICAL_HUD4_TEACHING_PATH,
  bannerText:
    "ClinicalHUD 4 — listen, then name the differential. Simulated case. Do not enter real patient information.",
});
